import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Badge, Button, Empty, Flex, Input, List, Select, Space, Spin, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  BellOutlined,
  LinkOutlined,
  NotificationOutlined,
  ReloadOutlined,
  RobotOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAdminMessenger } from '../../hooks/useAdminMessenger';
import InboxNotificationsModal from '../../components/InboxNotificationsModal/InboxNotificationsModal.component';
import MessengerSearch from '../../components/MessengerSearch/MessengerSearch.component';
import MessengerMessageButtons from '../../components/MessengerMessageButtons/MessengerMessageButtons.component';
import MessengerJoinLinkModal from '../../components/MessengerJoinLinkModal/MessengerJoinLinkModal.component';
import {
  MAX_TELEGRAM_MESSAGE_LENGTH,
  TelegramCallbackAnswerEntity,
  TelegramDialogKind,
  TelegramDialogMessageEntity,
  TelegramInboxSettingsEntity,
  TelegramJoinTargetEntity,
  TelegramMessageButtonEntity,
  TelegramPeerCardEntity,
  isSafeExternalUrl,
  telegramDialogKey,
  telegramMessagePreview,
} from '../../../domain/entities/TelegramMessenger';

import './AdminMessengerPage.styles.css';

const DIALOG_KIND_ICONS: Record<TelegramDialogKind, React.ReactNode> = {
  user: <UserOutlined />,
  bot: <RobotOutlined />,
  group: <TeamOutlined />,
  channel: <NotificationOutlined />,
};

const STICK_TO_BOTTOM_THRESHOLD_PX = 80;

const AdminMessengerPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const {
    accounts,
    accountUuid,
    dialogs,
    dialogKey,
    messages,
    hasMoreMessages,
    settings,
    isLoadingAccounts,
    isLoadingDialogs,
    isLoadingMessages,
    isSending,
    isSavingSettings,
    searchResults,
    isSearching,
    error,
    loadDialogs,
    loadOlderMessages,
    send,
    saveSettings,
    search,
    clearSearch,
    openPeer,
    join,
    pressButton,
    openAccount,
    openDialog,
  } = useAdminMessenger();

  const [draft, setDraft] = useState('');
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isJoinLinkOpen, setJoinLinkOpen] = useState(false);
  const [isJoiningLink, setJoiningLink] = useState(false);

  const dialog = dialogs.find((d) => telegramDialogKey(d) === dialogKey);
  const chronological = useMemo(() => [...messages].reverse(), [messages]);
  const newestMessageId = messages[0]?.id;
  const messagesRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const scrolledDialogKey = useRef<string>();

  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el || !newestMessageId) return;
    if (scrolledDialogKey.current !== dialogKey) stickToBottom.current = true;
    if (!stickToBottom.current) return;
    el.scrollTop = el.scrollHeight;
    scrolledDialogKey.current = dialogKey;
  }, [dialogKey, newestMessageId]);

  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_THRESHOLD_PX;
  };
  const timeFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }), [i18n.language]);

  const translateError = (slug: string) => t(slug, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) });
  const showError = (e: unknown) => message.error(translateError(e instanceof Error ? e.message : 'unknown-error'));

  const messagePreview = (m: TelegramDialogMessageEntity) => {
    const preview = telegramMessagePreview(m);
    if (!m.text.trim() && m.mediaType) return t(`messenger-media-${m.mediaType}`, { ns: 'labels', defaultValue: preview });
    return preview;
  };

  const handleSend = async () => {
    try {
      await send(draft);
      setDraft('');
    } catch (e) {
      showError(e);
    }
  };

  const handleOpenPeer = (card: TelegramPeerCardEntity) => openDialog(openPeer(card));

  const handleJoin = async (target: TelegramJoinTargetEntity) => {
    try {
      const result = await join(target);
      if (result.status === 'request_pending') message.info(t('messenger-join-request-pending', { ns: 'messages' }));
      else message.success(t(result.status === 'opened' ? 'messenger-join-opened' : 'messenger-join-joined', { ns: 'messages' }));
      if (result.dialogKey) openDialog(result.dialogKey);
      return true;
    } catch (e) {
      showError(e);
      return false;
    }
  };

  const handleJoinLink = async (link: string) => {
    setJoiningLink(true);
    try {
      if (await handleJoin({ link })) setJoinLinkOpen(false);
    } finally {
      setJoiningLink(false);
    }
  };

  const showCallbackAnswer = (answer: TelegramCallbackAnswerEntity) => {
    if (!answer.message && !answer.url) {
      message.success(t('messenger-button-pressed', { ns: 'messages' }));
      return;
    }
    const content = (
      <>
        {answer.message && <div className="admin-messenger-callback-text">{answer.message}</div>}
        {answer.url && isSafeExternalUrl(answer.url) && (
          <a href={answer.url} target="_blank" rel="noopener noreferrer">{t('messenger-open-link', { ns: 'buttons' })}</a>
        )}
      </>
    );
    if (answer.alert) modal.info({ content });
    else message.info(content);
  };

  const handlePressButton = async (messageId: string, button: TelegramMessageButtonEntity) => {
    try {
      showCallbackAnswer(await pressButton(messageId, button));
    } catch (e) {
      showError(e);
    }
  };

  const handleSendText = async (text: string) => {
    try {
      await send(text);
    } catch (e) {
      showError(e);
    }
  };

  const handleSaveSettings = async (value: TelegramInboxSettingsEntity) => {
    try {
      await saveSettings(value);
      message.success(t('messenger-inbox-settings-saved', { ns: 'messages' }));
      setSettingsOpen(false);
    } catch (e) {
      showError(e);
    }
  };

  const accountOptions = accounts.map((account) => ({
    value: account.uuid,
    label: account.phone,
    disabled: account.status !== 'AUTHENTICATED',
  }));

  return (
    <div className="admin-messenger-page">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
        <div>
          <h1 className="admin-messenger-title">{t('admin-messenger', { ns: 'titles' })}</h1>
          <p className="admin-messenger-subtitle">{t('admin-messenger-subtitle', { ns: 'titles' })}</p>
        </div>
        <Space wrap>
          <Select
            className="admin-messenger-account-select"
            placeholder={t('messenger-select-account', { ns: 'labels' })}
            options={accountOptions}
            value={accountUuid}
            loading={isLoadingAccounts}
            onChange={(uuid: string) => openAccount(uuid)}
            showSearch
            optionFilterProp="label"
          />
          <Button icon={<ReloadOutlined />} disabled={!accountUuid} loading={isLoadingDialogs} onClick={() => loadDialogs()}>
            {t('refresh', { ns: 'buttons' })}
          </Button>
          <Button icon={<LinkOutlined />} disabled={!accountUuid} onClick={() => setJoinLinkOpen(true)}>
            {t('messenger-join-by-link', { ns: 'buttons' })}
          </Button>
          <Button icon={<BellOutlined />} disabled={!accountUuid} onClick={() => setSettingsOpen(true)}>
            {t('messenger-inbox-notifications', { ns: 'buttons' })}
            {settings?.enabled && <Badge status="success" className="admin-messenger-bell-badge" />}
          </Button>
        </Space>
      </Flex>

      {error && <Alert type="error" showIcon message={translateError(error)} />}

      {!accountUuid ? (
        <Empty description={t('messenger-no-account', { ns: 'labels' })} />
      ) : (
        <div className={`admin-messenger-body${dialog ? ' admin-messenger-body-chat-open' : ''}`}>
          <div className="admin-messenger-dialogs">
            <MessengerSearch
              key={accountUuid}
              results={searchResults}
              isSearching={isSearching}
              kindIcons={DIALOG_KIND_ICONS}
              onSearch={search}
              onClear={clearSearch}
              onOpen={handleOpenPeer}
              onJoin={async (card) => {
                await handleJoin({ peer: card });
              }}
            />
            <List
              loading={isLoadingDialogs && dialogs.length === 0}
              dataSource={dialogs}
              locale={{ emptyText: t('messenger-dialogs-empty', { ns: 'labels' }) }}
              rowKey={(d) => telegramDialogKey(d)}
              renderItem={(d) => (
                <List.Item
                  className={`admin-messenger-dialog${telegramDialogKey(d) === dialogKey ? ' admin-messenger-dialog-active' : ''}`}
                  onClick={() => openDialog(telegramDialogKey(d))}
                >
                  <List.Item.Meta
                    title={
                      <Flex justify="space-between" align="center" gap={8}>
                        <Typography.Text strong ellipsis>
                          <span className="admin-messenger-kind-icon" title={t(`messenger-kind-${d.kind}`, { ns: 'labels' })}>{DIALOG_KIND_ICONS[d.kind]}</span>
                          {d.title}
                        </Typography.Text>
                        {d.unreadCount > 0 && <Badge count={d.unreadCount} />}
                      </Flex>
                    }
                    description={
                      <>
                        {d.username && <Typography.Text type="secondary" className="admin-messenger-username">@{d.username}</Typography.Text>}
                        {d.lastMessage && (
                          <Typography.Text type="secondary" ellipsis className="admin-messenger-dialog-preview">
                            {d.lastMessage.out && `${t('messenger-you', { ns: 'labels' })}: `}
                            {messagePreview(d.lastMessage)}
                          </Typography.Text>
                        )}
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          </div>

          <div className="admin-messenger-chat">
            {!dialog ? (
              <Flex className="admin-messenger-chat-placeholder" justify="center" align="center">
                <Empty description={t('messenger-select-dialog', { ns: 'labels' })} />
              </Flex>
            ) : (
              <>
                <Flex className="admin-messenger-chat-header" align="center" gap={8}>
                  <Button
                    className="admin-messenger-back"
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    aria-label={t('back', { ns: 'buttons' })}
                    onClick={() => openDialog(undefined)}
                  />
                  <span className="admin-messenger-kind-icon" title={t(`messenger-kind-${dialog.kind}`, { ns: 'labels' })}>{DIALOG_KIND_ICONS[dialog.kind]}</span>
                  <Typography.Text strong>{dialog.title}</Typography.Text>
                  {dialog.username && <Typography.Text type="secondary">@{dialog.username}</Typography.Text>}
                </Flex>

                <div className="admin-messenger-messages" ref={messagesRef} onScroll={handleMessagesScroll}>
                  {hasMoreMessages && (
                    <Flex justify="center">
                      <Button size="small" loading={isLoadingMessages} onClick={() => loadOlderMessages()}>
                        {t('load-more', { ns: 'buttons' })}
                      </Button>
                    </Flex>
                  )}
                  {isLoadingMessages && messages.length === 0 && (
                    <Flex justify="center"><Spin /></Flex>
                  )}
                  {!isLoadingMessages && messages.length === 0 && (
                    <Empty description={t('messenger-messages-empty', { ns: 'labels' })} />
                  )}
                  {chronological.map((m) => (
                    <div key={m.id} className={`admin-messenger-message ${m.out ? 'admin-messenger-message-out' : 'admin-messenger-message-in'}`}>
                      {!m.out && m.senderName && dialog.kind !== 'user' && (
                        <div className="admin-messenger-message-sender">{m.senderName}</div>
                      )}
                      <div className={`admin-messenger-message-text${!m.text.trim() ? ' admin-messenger-message-media' : ''}`}>
                        {messagePreview(m)}
                      </div>
                      {m.buttons && (
                        <MessengerMessageButtons
                          buttons={m.buttons}
                          canSendText={dialog.canSend}
                          onPress={(button) => handlePressButton(m.id, button)}
                          onSendText={handleSendText}
                          onJoinLink={async (url) => {
                            await handleJoin({ link: url });
                          }}
                        />
                      )}
                      <div className="admin-messenger-message-time">{timeFormat.format(new Date(m.sentAt))}</div>
                    </div>
                  ))}
                </div>

                {dialog.canSend ? (
                  <Flex className="admin-messenger-composer" gap={8} align="flex-end">
                    <Input.TextArea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={t('messenger-message-placeholder', { ns: 'labels' })}
                      autoSize={{ minRows: 1, maxRows: 6 }}
                      maxLength={MAX_TELEGRAM_MESSAGE_LENGTH}
                      onPressEnter={(e) => {
                        if (e.shiftKey || e.nativeEvent.isComposing) return;
                        e.preventDefault();
                        if (draft.trim() && !isSending) handleSend();
                      }}
                    />
                    <Button type="primary" icon={<SendOutlined />} loading={isSending} disabled={!draft.trim()} onClick={handleSend}>
                      {t('send', { ns: 'buttons' })}
                    </Button>
                  </Flex>
                ) : (
                  <Alert className="admin-messenger-read-only" type="info" showIcon message={t('messenger-read-only', { ns: 'labels' })} />
                )}
              </>
            )}
          </div>
        </div>
      )}

      <MessengerJoinLinkModal
        open={isJoinLinkOpen}
        isJoining={isJoiningLink}
        onCancel={() => setJoinLinkOpen(false)}
        onSubmit={handleJoinLink}
      />

      <InboxNotificationsModal
        open={isSettingsOpen}
        settings={settings}
        isSaving={isSavingSettings}
        onCancel={() => setSettingsOpen(false)}
        onSubmit={handleSaveSettings}
      />
    </div>
  );
};

export default AdminMessengerPage;
